import { CSSProperties, Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { formatCurrency } from 'support/helpers/currency/currency';
import { Divider } from 'components/Divider/Divider';
import { InfoPopover, InfoPopoverBody, InfoPopoverFooter } from 'components/InfoPopover/InfoPopover';

type Modification = {
  type?: 'RELATIVE' | 'ABSOLUTE';
  description?: string;
  reason_code?: 'SHIPPING' | 'PACKAGING' | 'HANDLING' | 'DISCOUNT' | 'INSURANCE' | 'ROUNDING';
  percentage?: number;
  amount?: number;
  tax?: {
    total_tax_amount?: number;
    items?: Array<{
      taxable_amount?: number;
      tax_amount?: number;
      tax_percentage?: number;
      tax_type?: 'VAT' | 'EXEMPT' | 'ZERO_RATED';
    }>;
  };
};
type ModificationGroup = {
  basis?: number;
  comments?: string;
  charges?: Array<Modification>;
  allowances?: Array<Modification>;
};

export const ModificationGroupsModal = ({
  modificationGroups,
  documentCurrency,
  totalAllowances,
  totalCharges,
  style,
  ref,
}: {
  modificationGroups: Array<ModificationGroup>;
  documentCurrency: string;
  totalAllowances: number;
  totalCharges: number;
  style?: CSSProperties;
  ref?: Ref<HTMLDivElement>;
}) => {
  return (
    <InfoPopover ref={ref} style={style}>
      <InfoPopoverBody>
        <ModificationGroupsModalTitle />
        <div className="space-y-2.5">
          {modificationGroups.map((modificationGroup, index) => (
            <ModificationGroupsLevel
              key={index}
              modificationGroup={modificationGroup}
              index={index}
              currency={documentCurrency}
            />
          ))}
        </div>
      </InfoPopoverBody>
      <ModificationGroupsModalFooter
        totalAllowances={totalAllowances}
        totalCharges={totalCharges}
        currency={documentCurrency}
      />
    </InfoPopover>
  );
};

const ModificationGroupsModalTitle = () => {
  const { t } = useTranslation();
  return (
    <h1 className="text-sm leading-tight font-bold text-gray-900">
      {t('webedi:line_items.modification_groups.modal.title')}
    </h1>
  );
};

const ModificationGroupsModalFooter = ({
  totalAllowances,
  totalCharges,
  currency,
}: {
  totalAllowances: number;
  totalCharges: number;
  currency: string;
}) => {
  const { t } = useTranslation();
  const total = totalCharges - totalAllowances;
  return (
    <InfoPopoverFooter>
      <div className="flex w-full items-center justify-between p-2">
        <div className="text-xs text-gray-500">{t('webedi:line_items.modification_groups.modal.footer')}</div>
        <div className="text-xs font-semibold text-gray-900">
          {formatCurrency(total, currency, { signDisplay: 'exceptZero' })}
        </div>
      </div>
    </InfoPopoverFooter>
  );
};

const ModificationGroupsLevel = ({
  modificationGroup,
  index,
  currency,
}: {
  modificationGroup: ModificationGroup;
  index: number;
  currency: string;
}) => {
  const { t } = useTranslation();
  return (
    <div>
      <Divider>
        <div className="-ml-8 bg-white py-3.5 pr-2 text-xs font-semibold tracking-tight text-gray-400 uppercase">
          {t('webedi:line_items.modification_groups.modal.level_title', { level: index + 1 })}
        </div>
      </Divider>
      {modificationGroup.charges
        ?.filter((modification) => modification.amount || modification.percentage)
        .map((modification, index) => (
          <Modification key={index} modification={modification} type="charge" currency={currency} />
        ))}
      {modificationGroup.allowances
        ?.filter((modification) => modification.amount || modification.percentage)
        .map((modification, index) => (
          <Modification key={index} modification={modification} type="allowance" currency={currency} />
        ))}
    </div>
  );
};

const Modification = ({
  modification,
  type,
  currency,
}: {
  modification: Modification;
  type: 'charge' | 'allowance';
  currency: string;
}) => {
  const { t } = useTranslation();

  const reason = modification.reason_code
    ? t(`webedi:line_items.modification_groups.modal.modification.reason_code.${modification.reason_code}`)
    : undefined;

  return (
    <div className="py-3">
      <div className="grid grid-cols-3 gap-2 text-xs text-gray-900">
        <div>{t(`webedi:line_items.modification_groups.modal.modification.${type}`)}</div>
        <div>{reason}</div>
        <div className="text-end">
          {type === 'charge' ? '+' : '-'}
          {modification.percentage
            ? `${modification.percentage}%`
            : `${formatCurrency(modification.amount || 0, currency)}`}
        </div>
      </div>
      {modification.description && (
        <div className="mt-1 grid grid-cols-6 gap-2 text-xs text-gray-500">
          <div className="col-span-5">{modification.description}</div>
          <div></div>
        </div>
      )}
    </div>
  );
};

/**
 * i18n keys
 * t('webedi:line_items.modification_groups.modal.modification.charge')
 * t('webedi:line_items.modification_groups.modal.modification.allowance')
 * t('webedi:line_items.modification_groups.modal.modification.reason_code.SHIPPING')
 * t('webedi:line_items.modification_groups.modal.modification.reason_code.PACKAGING')
 * t('webedi:line_items.modification_groups.modal.modification.reason_code.HANDLING')
 * t('webedi:line_items.modification_groups.modal.modification.reason_code.DISCOUNT')
 * t('webedi:line_items.modification_groups.modal.modification.reason_code.INSURANCE')
 * t('webedi:line_items.modification_groups.modal.modification.reason_code.ROUNDING')
 */
